import {
  READ_ROWS_CAP,
  type DryRunFailure,
  type ReadDetail,
  type ReadResult,
  type BoardListing,
  type StoredPanelStatus,
  type StoredStage,
  type StoredControlStatus,
} from "@/shared/domain/boards/boardAuthoringService";

import { ApprovalMode } from "./approvalModes";
import {
  boardToolDeps,
  withAuthoringService,
  withBoardAccess,
} from "./boardAuthoringDeps";
import {
  describeDefinitionShapeProblem,
  isPlainObject,
} from "./boardToolSchema";
import {
  type ClientToolDeps,
  type ClientToolExecutionResult,
  createClientTool,
  toolError,
  ToolErrorKind,
  toolSuccess,
} from "./ClientTools";

export interface ReadBoardInput {
  board_id?: unknown;
  panel_id?: unknown;
  detail?: unknown;
  rows?: unknown;
  stage?: unknown;
  columns?: unknown;
  name_contains?: unknown;
  interaction_id?: unknown;
  proposed_definition?: unknown;
}

/** Most boards one list read returns. */
const BOARD_LIST_CAP = 50;

/** Most columns each sampled row carries unless `columns` names the ones wanted. */
const READ_COLUMNS_CAP = 40;

function isRowCount(value: unknown): value is number {
  return (
    typeof value === "number" &&
    Number.isInteger(value) &&
    value >= 1 &&
    value <= READ_ROWS_CAP
  );
}

const READ_DETAILS: readonly ReadDetail[] = ["summary", "full"];

function isReadDetail(value: unknown): value is ReadDetail {
  return (
    typeof value === "string" &&
    (READ_DETAILS as readonly string[]).includes(value)
  );
}

function isStringList(value: unknown): value is string[] {
  return (
    Array.isArray(value) &&
    value.length > 0 &&
    value.every((entry) => typeof entry === "string" && entry !== "")
  );
}

function describeInputProblem(input: ReadBoardInput): string | null {
  if (input.board_id !== undefined && typeof input.board_id !== "string") {
    return "board_id must be a string.";
  }
  if (input.panel_id !== undefined && typeof input.panel_id !== "string") {
    return "panel_id must be a string.";
  }
  if (input.panel_id !== undefined && input.board_id === undefined) {
    return "panel_id needs a board_id to look the panel up in.";
  }
  if (
    input.name_contains !== undefined &&
    typeof input.name_contains !== "string"
  ) {
    return "name_contains must be a string.";
  }
  if (input.name_contains !== undefined && input.board_id !== undefined) {
    return "name_contains filters the list of boards; omit board_id to use it.";
  }
  if (input.detail !== undefined && !isReadDetail(input.detail)) {
    return 'detail must be "summary" or "full".';
  }
  if (input.detail !== undefined && input.board_id === undefined) {
    return "detail applies to a board_id; omit both to list the boards.";
  }
  if (input.rows !== undefined && !isRowCount(input.rows)) {
    return `rows must be a whole number from 1 to ${READ_ROWS_CAP}.`;
  }
  if (input.rows !== undefined && input.panel_id === undefined) {
    return "rows needs a panel_id: the rows come from one of the stages that panel binds.";
  }
  if (input.stage !== undefined && typeof input.stage !== "string") {
    return "stage must be a string: the id of one of the stages the panel binds.";
  }
  if (input.stage !== undefined && input.rows === undefined) {
    return "stage applies with rows; say how many rows of that stage to return.";
  }
  if (input.columns !== undefined && !isStringList(input.columns)) {
    return "columns must be a non-empty array of column names.";
  }
  if (input.columns !== undefined && input.rows === undefined) {
    return "columns applies with rows; say how many rows to return.";
  }
  return null;
}

// ---------------------------------------------------------------------------
// Results
// ---------------------------------------------------------------------------

/** One board of the list, in the spelling the tool's inputs use. */
function listingResult(board: BoardListing): Record<string, unknown> {
  return {
    id: board.id,
    name: board.name,
    ...(board.title !== undefined ? { title: board.title } : {}),
    draft: board.draft,
    changed_at: board.changedAt,
    panel_count: board.panelCount,
  };
}

/**
 * The list of boards, most recently changed first, narrowed to the names
 * holding `nameContains` and cut to {@link BOARD_LIST_CAP}. A cut list says
 * how many matched and how to reach the rest.
 */
function listResult(
  boards: BoardListing[],
  nameContains: string | undefined,
): Record<string, unknown> {
  const text = nameContains?.trim() ?? "";
  const needle = text.toLowerCase();
  const matching = boards
    .filter(
      (board) =>
        board.name.toLowerCase().includes(needle) ||
        (board.title?.toLowerCase().includes(needle) ?? false),
    )
    .sort((a, b) => Date.parse(b.changedAt) - Date.parse(a.changedAt));
  const shown = matching.slice(0, BOARD_LIST_CAP);
  const scope =
    text === ""
      ? "every board the user can read"
      : `the boards the user can read whose name or title contains "${text}"`;
  return {
    status: "list",
    covers: `${scope}, most recently changed first`,
    total: matching.length,
    boards: shown.map(listingResult),
    ...(shown.length < matching.length
      ? {
          truncated: true,
          note: `Showing the ${shown.length} most recently changed of ${matching.length} boards. Pass name_contains with part of a board's name to find one that is not shown.`,
        }
      : {}),
  };
}

/** A failure with its position lists under snake_case names. */
function failureResult(failure: DryRunFailure): Record<string, unknown> {
  const { rootIndices, ...rest } = failure;
  return {
    ...rest,
    ...(rootIndices !== undefined ? { root_indices: rootIndices } : {}),
  };
}

/** One panel's status: what it stores, whether it parses, and the stage cards it shows on screen. */
function panelStatusResult(panel: StoredPanelStatus): Record<string, unknown> {
  const { interactionDiagnostics, parse, stageCards, ...rest } = panel;
  return {
    ...rest,
    parse: parse === "valid" ? parse : failureResult(parse),
    ...(interactionDiagnostics !== undefined
      ? { interaction_diagnostics: interactionDiagnostics }
      : {}),
    ...(stageCards !== undefined ? { stage_cards: stageCards } : {}),
  };
}

function stageResult(stage: StoredStage): Record<string, unknown> {
  const { failure, ...rest } = stage;
  return {
    ...rest,
    ...(failure !== undefined ? { failure: failureResult(failure) } : {}),
  };
}

/** One control: its stage as stored, the panels that read it, and the
 *  value it holds while on screen. */
function controlResult(control: StoredControlStatus): Record<string, unknown> {
  const { onScreen, uses, ...rest } = control;
  const stored = {
    ...rest,
    panels_reading: uses.map(({ panelId, rows, byName, places }) => ({
      panel_id: panelId,
      rows_filtered: rows,
      by_name: byName,
      by_name_in: places.map((place) =>
        "stageId" in place
          ? { place: place.kind, stage_id: place.stageId }
          : { place: place.kind },
      ),
    })),
  };
  if (onScreen === undefined) {
    return stored;
  }
  const { taggedValues, ...value } = onScreen;
  return {
    ...stored,
    on_screen: {
      ...value,
      ...(taggedValues !== undefined ? { tagged_values: taggedValues } : {}),
    },
  };
}

/**
 * The sampled rows with at most {@link READ_COLUMNS_CAP} columns each, or
 * only the columns asked for. A cut says which columns were left out, and a
 * requested column the stage does not have is named.
 */
function rowsResult(
  rows: Record<string, unknown>[],
  requested: string[] | undefined,
): Record<string, unknown> {
  // The column names come from the rows, so a read with no rows has none to
  // check a request against.
  if (rows.length === 0) {
    return { rows };
  }
  const available = [...new Set(rows.flatMap((row) => Object.keys(row)))];
  const pick = (names: string[]) =>
    rows.map((row) =>
      Object.fromEntries(
        names.filter((name) => name in row).map((name) => [name, row[name]]),
      ),
    );
  if (requested !== undefined) {
    const missing = requested.filter((name) => !available.includes(name));
    return {
      rows: pick(requested),
      ...(missing.length > 0
        ? {
            columns_note: `The stage has no column named ${missing.map((name) => `"${name}"`).join(", ")}. Its columns are: ${available.join(", ")}.`,
          }
        : {}),
    };
  }
  if (available.length <= READ_COLUMNS_CAP) {
    return { rows };
  }
  const kept = available.slice(0, READ_COLUMNS_CAP);
  return {
    rows: pick(kept),
    columns_truncated: true,
    columns_note: `Each row shows the first ${kept.length} of the stage's ${available.length} columns. Pass columns with the names you need. Left out: ${available.slice(READ_COLUMNS_CAP).join(", ")}.`,
  };
}

function panelResult(
  result: Extract<ReadResult, { status: "panel" }>,
  columns: string[] | undefined,
): Record<string, unknown> {
  return {
    status: result.status,
    board_id: result.boardId,
    panel: result.panel,
    parse: panelStatusResult(result.parse),
    ...(result.rows !== undefined ? rowsResult(result.rows, columns) : {}),
    ...(result.rowsTotal !== undefined ? { rows_total: result.rowsTotal } : {}),
    ...(result.rowsNote !== undefined ? { rows_note: result.rowsNote } : {}),
    ...(result.interactionResults !== undefined
      ? { interaction_results: result.interactionResults }
      : {}),
    ...(result.markLineageOutputs !== undefined
      ? { markLineageOutputs: result.markLineageOutputs }
      : {}),
  };
}

function boardResult(
  result: Extract<ReadResult, { status: "board" }>,
): Record<string, unknown> {
  const {
    boardId,
    changedAt,
    draft,
    specsElided,
    invalidTopLevel,
    status,
    name,
    title,
    timeRange,
    stages,
    panels,
    controls,
    onScreenBoard,
    ...rest
  } = result;
  return {
    status,
    board_id: boardId,
    name,
    ...(title !== undefined ? { title } : {}),
    draft,
    changed_at: changedAt,
    // The dry-run report names these problems `definition_problems` too.
    ...(invalidTopLevel !== undefined
      ? { definition_problems: invalidTopLevel.issues }
      : {}),
    time_range: timeRange,
    ...rest,
    ...(stages !== undefined ? { stages: stages.map(stageResult) } : {}),
    panels: panels.map(panelStatusResult),
    ...(controls !== undefined
      ? { controls: controls.map(controlResult) }
      : {}),
    ...(specsElided !== undefined ? { specs_elided: specsElided } : {}),
    ...(onScreenBoard !== undefined ? { on_screen_board: onScreenBoard } : {}),
  };
}

// ---------------------------------------------------------------------------
// Checking one interaction of a proposed definition
// ---------------------------------------------------------------------------

/** The string ids of the objects in a list as the chat sent it. */
function idsOf(list: unknown): string[] {
  return Array.isArray(list)
    ? list.flatMap((entry) =>
        isPlainObject(entry) && typeof entry.id === "string" ? [entry.id] : [],
      )
    : [];
}

function quoted(ids: string[]): string {
  return ids.length === 0 ? "none" : ids.map((id) => `"${id}"`).join(", ");
}

/**
 * Why the authoring service returned no preview of the interaction, worked out
 * from the proposed definition as sent. The service returns nothing for
 * several causes; this function tells them apart so the model knows what to
 * fix.
 */
function describeUnreadableInteraction(
  definition: Record<string, unknown>,
  panelId: string,
  interactionId: string,
): string {
  const shapeProblem = describeDefinitionShapeProblem(definition);
  if (shapeProblem !== null) {
    return `proposed_definition is not a board definition, so the interaction was not checked. ${shapeProblem.message}`;
  }
  const panels: unknown[] = Array.isArray(definition.panels)
    ? definition.panels
    : [];
  const panel = panels.find(
    (entry) => isPlainObject(entry) && entry.id === panelId,
  );
  if (!isPlainObject(panel)) {
    return `proposed_definition has no panel with id "${panelId}"; its panel ids are ${quoted(idsOf(panels))}.`;
  }
  if (!idsOf(panel.interactions).includes(interactionId)) {
    return `Panel "${panelId}" of proposed_definition declares no interaction with id "${interactionId}"; its interaction ids are ${quoted(idsOf(panel.interactions))}.`;
  }
  return `proposed_definition or its panel "${panelId}" does not parse, so the interaction was not checked. Call client_dry_run_board with the same definition, fix what its report lists, and check the interaction again.`;
}

async function execute(
  input: ReadBoardInput,
  deps?: ClientToolDeps,
): Promise<ClientToolExecutionResult> {
  if (
    input.interaction_id !== undefined ||
    input.proposed_definition !== undefined
  ) {
    const {
      interaction_id: interactionId,
      panel_id: panelId,
      proposed_definition: definition,
    } = input;
    if (
      typeof interactionId !== "string" ||
      typeof panelId !== "string" ||
      !isPlainObject(definition)
    ) {
      return toolError(
        "Checking an interaction of a proposed definition takes interaction_id, panel_id and proposed_definition together. It runs no query and changes nothing.",
        {},
        ToolErrorKind.VALIDATION,
      );
    }
    return withAuthoringService((service) => {
      const preview = service.previewInteraction({
        definition,
        panelId,
        interactionId,
      });
      if (preview === undefined) {
        return toolError(
          describeUnreadableInteraction(definition, panelId, interactionId),
          { panel_id: panelId, interaction_id: interactionId },
          ToolErrorKind.VALIDATION,
        );
      }
      const { interaction: checkedInteraction, ...check } = preview;
      return toolSuccess("checked_panel_interaction", {
        panel_id: panelId,
        interaction_id: interactionId,
        ...check,
        ...(checkedInteraction !== undefined
          ? { checked_interaction: checkedInteraction }
          : {}),
      });
    });
  }
  const problem = describeInputProblem(input);
  if (problem !== null) {
    return toolError(problem, {}, ToolErrorKind.VALIDATION);
  }
  const boardId =
    typeof input.board_id === "string" ? input.board_id : undefined;
  const panelId =
    typeof input.panel_id === "string" ? input.panel_id : undefined;
  const detail = isReadDetail(input.detail) ? input.detail : undefined;
  const rows = isRowCount(input.rows) ? input.rows : undefined;
  const stage = typeof input.stage === "string" ? input.stage : undefined;
  const columns = isStringList(input.columns) ? input.columns : undefined;
  const nameContains =
    typeof input.name_contains === "string" ? input.name_contains : undefined;
  return withBoardAccess(boardToolDeps(deps), async (service, boards) => {
    const result = await service.read(
      { boardId, panelId, detail, rows, stage },
      boards,
    );
    switch (result.status) {
      case "not-found": {
        return toolError(
          `No board with id "${result.boardId}" is available to you: it does not exist, or it belongs to someone else and is not shared. Call this tool without board_id to list the ones you can read.`,
          { board_id: result.boardId },
          ToolErrorKind.NOT_FOUND,
        );
      }
      case "panel-not-found": {
        return toolError(
          `Board "${result.boardId}" has no panel with id "${result.panelId}".`,
          {
            board_id: result.boardId,
            panel_id: result.panelId,
            panel_ids: result.panelIds,
          },
          ToolErrorKind.NOT_FOUND,
        );
      }
      case "list": {
        return toolSuccess(
          "read_board",
          listResult(result.boards, nameContains),
        );
      }
      case "board": {
        return toolSuccess("read_board", boardResult(result));
      }
      case "panel": {
        return toolSuccess("read_board", panelResult(result, columns));
      }
    }
  });
}

/**
 * `client_read_board`: lists the boards the person can open, or reads one of
 * them. The board on screen also reaches the model in the viewing context sent
 * with every message; this tool reads what that context leaves out. The
 * description below is the contract the model reads. A read stays small
 * unless it asks for the whole definition: lists stop at
 * {@link BOARD_LIST_CAP} boards and sampled rows at {@link READ_COLUMNS_CAP}
 * columns.
 *
 * A result's members are snake_case, like the tools' inputs and the write
 * tools' results. The model sends three kinds of object back as they are, so
 * they keep their own spelling:
 * 1. Stored definition content (a panel, a control's stage body, the
 *    definition), camelCase by the definition's schema.
 * 2. The objects under `on_screen_board`, `interaction_results` and
 *    `markLineageOutputs`, which `client_publish_board_clause` takes
 *    unchanged inside `command`.
 * 3. `clauses`, `PublishedClause` objects in the selection store's clause
 *    shape.
 *
 * Read-only, so it auto-executes by default.
 */
export const tool = createClientTool<ReadBoardInput>({
  name: "client_read_board",
  description:
    "Reads the user's boards, their own and the ones shared with their " +
    "organization, and changes nothing. " +
    "With no board_id it lists them, most recently changed first: id, name, " +
    "title, draft, changed_at (ISO 8601 UTC) and panel_count; at most " +
    `${BOARD_LIST_CAP} are returned, and name_contains narrows the list. ` +
    "draft is true for a board draft. " +
    "With board_id it returns a summary: the time_range; the calendar " +
    "(time_zone and week_start); the sections; each " +
    "stage's id, name, kind and inputs, without its body; each panel's " +
    "id, kind, title, placement, the ids of the stages it binds, its " +
    "participation, publish and interactions, and parse ('valid' or the " +
    "failure); and the controls, each with its stage id, name, body, " +
    "caption and panels_reading: each panel that reads the control, with " +
    "rows_filtered ('all', 'some' or 'none' of the stages it shows pass " +
    "through a control-filter of it), by_name (its text or a stage it " +
    "reads names the control as $name) and by_name_in, each place that " +
    "does: place 'title' or 'spec' of the panel, or 'sql', 'expressions', " +
    "'group-by' or 'field-path' of the stage stage_id. While that board is on " +
    "screen in this tab the summary also carries each panel's " +
    "stage_cards (each stage the panel shows a card for in place of rows: " +
    "it failed, waits for a value in a control, or was stopped before it " +
    "had rows), the value each control holds, clauses, what is " +
    "selected and by which panel or the chat, and on_screen_board, the " +
    "instanceId and definitionDigest a client_publish_board_clause " +
    "command takes; when they are absent their " +
    "state is unknown, not empty. definition_problems, when present, " +
    "lists why the definition's top-level members cannot be read. " +
    "detail 'full' adds the board's definition, which is the read to " +
    "make before editing stages. When the definition's JSON is over " +
    "24 KiB, every " +
    "chart spec in it, small ones included, is replaced by a note, and " +
    "each panel with an id is listed in specs_elided: read that panel by " +
    "panel_id before sending it back, and never send the note as a spec. " +
    "With board_id and panel_id it returns that panel as the board holds it, the " +
    "form a client_update_board replace sends back. Adding rows samples " +
    "a stage the panel binds as the page fetched it, which works only " +
    "while the board is on screen; rows_note says which stage the rows " +
    "are from or why there are none. When the panel parses, a read with " +
    "rows also returns " +
    "interaction_results (each displayed binding's rows, result revision " +
    "and result context) and markLineageOutputs (each mark lineage " +
    "output's rows and the precondition an interaction on it carries), which a " +
    "client_publish_board_clause command is built from; their members, " +
    "like definition content, are camelCase because they are " +
    "sent back unchanged. " +
    "With proposed_definition, panel_id and interaction_id it checks one " +
    "interaction of a proposed definition, running no query: the result's " +
    "action is 'checked_panel_interaction', and it carries a status, " +
    "diagnostics and checked_interaction, the interaction as the board reads it. " +
    "While misc_context.board.editing is present, the panel editor holds " +
    "an unsaved draft of the panel it names, which this tool does not " +
    "return; read it with client_read_panel_draft.",
  input_schema: {
    type: "object",
    properties: {
      board_id: {
        type: "string",
        description: "The board to read. Omit to list boards.",
      },
      name_contains: {
        type: "string",
        description:
          "Without board_id: list only boards whose name or title " +
          "contains this text, ignoring case.",
      },
      panel_id: {
        type: "string",
        description:
          "With board_id: return this panel as the board holds it. A chart's spec " +
          "can be large, so read one panel at a time.",
      },
      detail: {
        type: "string",
        enum: [...READ_DETAILS],
        description:
          "With board_id and no panel_id: 'summary' (the default) or " +
          "'full', which adds the board's definition.",
      },
      rows: {
        type: "integer",
        minimum: 1,
        maximum: READ_ROWS_CAP,
        description:
          "With panel_id: how many rows to sample, in the stage's own " +
          "order. The sample can already be narrowed by selections that " +
          "apply when the stage fetches, and comes before anything the " +
          "panel itself does to the rows (a pivot, a chart transform). " +
          "interaction_results and markLineageOutputs share this row limit.",
      },
      stage: {
        type: "string",
        description:
          "With rows: the id of the bound stage to sample. Defaults to " +
          "the first stage the panel binds. It also limits " +
          "interaction_results and markLineageOutputs to the bindings of " +
          "that stage; omit it to get every binding's entry.",
      },
      columns: {
        type: "array",
        items: { type: "string" },
        description: `With rows: the columns to return. Without it a row carries at most its first ${READ_COLUMNS_CAP} columns and the result names the rest.`,
      },
      proposed_definition: {
        type: "object",
        description:
          "A complete board definition, with panel_id and interaction_id.",
      },
      interaction_id: {
        type: "string",
        description:
          "The id of the interaction in that panel of proposed_definition to check.",
      },
    },
  },
  execute,
  defaultApprovalMode: ApprovalMode.AUTO_EXECUTE,
  allowableApprovalModes: [
    ApprovalMode.REQUIRE_APPROVAL,
    ApprovalMode.FOLLOW_AUTO,
    ApprovalMode.AUTO_EXECUTE,
  ],
  displayName: "Read board",
  displayDescription:
    "Lists your boards, or returns one board's per-panel summary, one of its panels, or its whole definition to the chat. Changes nothing.",
});
